import log from '@vcclive/log';
import moment from 'moment';
import {Action, SagaApi} from '../../interfaces/redux';
import {isMessage, RootState} from './client-reducer';
import {LocalDataStore} from './LocalDataStore';
import {PluginSettings} from '../common';

/*
Flow
1. no settings (init not yet called)
2. got settings (pluginInitialized -> frameManager.init) -> store and check parentOrigin
3. send message to parent "we are loaded" (frame.loaded)
4. got cookie data from parent via postMessage (setLocalData)
5. init frame
 */
export class FrameManager {
    private readonly api: SagaApi<RootState>;
    private openTimeout: ReturnType<typeof setTimeout> | undefined;
    private parentOrigin = '';

    constructor(api: SagaApi<RootState>) {
        this.api = api;
    }

    public init = (settings: PluginSettings) => {
        log.debug('FrameManager init', settings);
        this.parentOrigin = this.validateParentOrigin(settings);
        if (!this.parentOrigin) {
            return;
        }
        LocalDataStore.setAllowedOrigin(this.parentOrigin);
        window.addEventListener('message', this.handleMessage, false);
        window.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.api.dispatch({type: 'close'});
            }
        });
        this.api.dispatch({type: 'frameLoaded'});
        this.sendToParent('frame.loaded');
    };

    public onClosed = () => {
        this.sendToParent('setSize', {
            width: null,
            height: null,
        });
        const {snooze, snoozeTime} = this.api.getState().root.settings;

        if (!this.hasConversationStarted() && snooze && snoozeTime && LocalDataStore.get('webchatSnoozed') !== '1') {
            const nextOpen = moment().add(snoozeTime, 'm');
            const expires: Date | undefined = nextOpen.toDate();
            const nextOpenDate = nextOpen.format();
            this.openChatWithTimeout(nextOpen.diff(moment(), 'ms'), true);
            LocalDataStore.set('webchatNextAutoOpenTime', nextOpenDate, {expires});
        }

        LocalDataStore.set('webchatPluginState', 'closed');
    };

    public initChatFrame = () => {
        const {settings} = this.api.getState().root;
        const nextAutoOpenTime = LocalDataStore.get('webchatNextAutoOpenTime');
        if (!LocalDataStore.get('webchatPluginState')) {
            if (settings.automaticOpen) {
                this.openChatWithTimeout(settings.automaticOpenTimeout * 1000);
                const nextOpen = moment().add(settings.automaticOpenTimeout, 's');
                LocalDataStore.set('webchatNextAutoOpenTime', nextOpen.format(), {expires: nextOpen.toDate()});
            }
        } else if (LocalDataStore.get('webchatPluginState') === 'opened') {
            this.openChat();
        } else if (!this.hasConversationStarted() && nextAutoOpenTime) {
            const nextOpen = moment(nextAutoOpenTime).diff(moment(), 's');
            const openTimeout = nextOpen > 0 ? nextOpen : 0;
            this.openChatWithTimeout(openTimeout * 1000, LocalDataStore.get('webchatPluginState') === 'closed');
        }
    };

    public onOpen = () => {
        this.sendToParent('setSize', {
            width: '400px',
            height: '500px',
        });
        if (this.openTimeout) {
            clearTimeout(this.openTimeout);
        }
        LocalDataStore.set('webchatPluginState', 'opened');
        LocalDataStore.remove('webchatNextAutoOpenTime');
    };

    public openChat = () => {
        const hasQueue = this.api.getState().root.queues.length > 0;
        const isWindowOpen = this.api.getState().root.showWindow;
        if (hasQueue && !isWindowOpen) {
            this.api.dispatch({type: 'open'});
        }
    };

    public sendToParent = (type: string, payload?: any) => {
        const action: Action = {type};
        if (!this.parentOrigin) {
            throw new Error('Unable to send message to parent window without knowing its origin!');
        }
        if (payload) {
            action.payload = payload;
        }
        window.parent.postMessage(action, this.parentOrigin);
    };

    private getAllowedOrigins = (settings: PluginSettings): string[] => {
        return (settings || this.api.getState().root.settings).allowedOrigins
            .split(/\n/)
            .filter(value => value.trim().length > 0);
    };

    private hasConversationStarted = () => {
        const isPlugin = /^webchat-plugin/;
        return this.api.getState().root.messages
            .filter(isMessage)
            .some(m => isPlugin.test(m.from));
    };

    private openChatWithTimeout = (timeout: number, isSnooze?: boolean) => {
        if (this.openTimeout) {
            clearTimeout(this.openTimeout);
        }
        this.openTimeout = setTimeout(() => {
            this.openChat();
            if (isSnooze) {
                LocalDataStore.set('webchatSnoozed', '1');
            }
        }, timeout);
    };

    private validateParentOrigin = (settings: PluginSettings) => {
        if (document.referrer.length === 0) {
            log.warn('Unable to start webchat because its referrer is empty!');
            return '';
        }
        const parentOrigin = new URL(document.referrer).origin;
        const allowed = this.getAllowedOrigins(settings);
        if (!allowed.includes(parentOrigin)) {
            log.warn('Message origin error', {
                parentOrigin,
                allowedOrigins: allowed,
            });
            return '';
        }

        return parentOrigin;
    };

    private handleMessage = (event: MessageEvent): void => {
        if (this.parentOrigin !== event.origin) {
            log.debug(`Blocked message with unallowed origin ${event.origin}!`);
            return;
        }

        switch (event.data?.type) {
            case 'setSiteInfo':
                this.api.dispatch({
                    type: 'setSiteInfo',
                    payload: {
                        url: event.data.payload.url,
                        title: event.data.payload.title,
                    },
                });
                break;
            case 'setLocalData':
                LocalDataStore.update(event.data.payload);
                this.initChatFrame();
                break;
        }
    };
}
